import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Typography,
    Box,
    Paper,
    Divider,
    IconButton,
    Tooltip,
    InputAdornment,
    Checkbox,
    FormControlLabel,
    Chip,
} from '@mui/material';
import { toast } from 'react-toastify';
import AppDialogTitle from '../../../../ui/appDialogTitle';
import { ContactLensConfigurationService, ValidDataService } from '../../../../../service/validDataService';
import { isLocalDev } from '../../../../../utils/environment';
import { formatAmountForDisplay } from '../../../../../utils';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RxSection from './RxSection';
import SelectRxDialog from '../../rx/selectRxDialog';
import CreateRxInlineDialog from '../../rx/createRxInline';
import { v4 as uuidv4 } from 'uuid'
import CustomerInfobar from '../../../customerInfobar';
//import { formatAmountForDisplays } from '../../../../../utils';

const INITIAL_EYE_CONFIG = {
    manufacturerId: '',
    contactLensModelId: '',
    scheduleId: '',
    boxQuantity: 1,
    pricing: null, // { lensesPerBox, baseCost, baseRetailPrice, totalCost, totalRetail }
    notes: '',
    useTrial: false,
};

export default function ContactLensConfigurationDialog({ open, onClose, onSave, selectedRx, existingContactLensData, customer }) {
    const [manufacturers, setManufacturers] = useState([]);
    // local RX state so user can select/create RX inside this dialog if not provided by parent
    const [localSelectedRx, setLocalSelectedRx] = useState(selectedRx || null);
    const [rxSelectDialogOpen, setRxSelectDialogOpen] = useState(false);
    const [createRxDialogOpen, setCreateRxDialogOpen] = useState(false);
    const [odContactLensModels, setOdContactLensModels] = useState([]);
    const [osContactLensModels, setOsContactLensModels] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(false);

    // Separate configurations for each eye
    const [odConfig, setOdConfig] = useState({ ...INITIAL_EYE_CONFIG });
    const [osConfig, setOsConfig] = useState({ ...INITIAL_EYE_CONFIG });

    // Load all reference data on mount or when dialog opens
    useEffect(() => {
        if (open) {
            loadReferenceData().then(() => {
                // Populate existing data AFTER reference data is loaded
                if (existingContactLensData) {
                    populateExistingData(existingContactLensData);
                } else {
                    // Reset to initial state if adding new
                    setOdConfig({ ...INITIAL_EYE_CONFIG });
                    setOsConfig({ ...INITIAL_EYE_CONFIG });
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, existingContactLensData]);

    // keep localSelectedRx in sync when parent prop changes
    useEffect(() => {
        setLocalSelectedRx(selectedRx || null);
    }, [selectedRx]);

    const loadReferenceData = async () => {
        try {
            // Fetch contact lens manufacturers that have pricing matrices
            const mfrResponse = await ValidDataService.getManufacturersWithPricingByType('contact_lens');
            if (mfrResponse?.code === 0) {
                setManufacturers(mfrResponse.data || []);
            }

            // Fetch schedules
            const scheduleResponse = await ContactLensConfigurationService.getActiveSchedules();
            if (scheduleResponse?.code === 0) {
                setSchedules(scheduleResponse.data || []);
            }
        } catch (error) {
            if (isLocalDev) console.error(error);
            toast.error('Failed to load reference data');
        }
        // Return promise to allow caller to know when data is loaded
        return Promise.resolve();
    };

    // Populate form with existing contact lens data for editing
    const populateExistingData = async (contactLensData) => {
        if (contactLensData.OD) {
            const odData = contactLensData.OD;
            setOdConfig({
                manufacturerId: odData.itemConfig?.manufacturerId || '',
                contactLensModelId: odData.itemConfig?.contactLensModelId || '',
                scheduleId: odData.itemConfig?.scheduleId || '',
                boxQuantity: odData.quantity || 1,
                pricing: odData.itemConfig?.pricing || null,
                notes: odData.itemConfig?.notes || '',
            });

            // Load models for OD manufacturer
            if (odData.itemConfig?.manufacturerId) {
                await handleManufacturerChange(odData.itemConfig.manufacturerId, 'OD');
                handleConfigChange('contactLensModelId', odData.itemConfig?.contactLensModelId || '', 'OD');
                handleConfigChange('useTrial', odData.isTrial || false, 'OD');
            }
        }

        if (contactLensData.OS) {
            const osData = contactLensData.OS;
            setOsConfig({
                manufacturerId: osData.itemConfig?.manufacturerId || '',
                contactLensModelId: osData.itemConfig?.contactLensModelId || '',
                scheduleId: osData.itemConfig?.scheduleId || '',
                boxQuantity: osData.quantity || 1,
                pricing: osData.itemConfig?.pricing || null,
                notes: osData.itemConfig?.notes || '',
            });

            // Load models for OS manufacturer
            if (osData.itemConfig?.manufacturerId) {
                await handleManufacturerChange(osData.itemConfig.manufacturerId, 'OS');
                handleConfigChange('contactLensModelId', osData.itemConfig?.contactLensModelId || '', 'OS');
                handleConfigChange('useTrial', osData.isTrial || false, 'OS');
            }
        }
        if (contactLensData.rx) {
            setLocalSelectedRx(contactLensData.rx);
        }
    };

    // Handle manufacturer change - load models
    const handleManufacturerChange = async (manufacturerId, eye) => {
        const updateConfig = eye === 'OD' ? setOdConfig : setOsConfig;
        const setModels = eye === 'OD' ? setOdContactLensModels : setOsContactLensModels;
        
        updateConfig(prev => ({
            ...prev,
            manufacturerId,
            contactLensModelId: '',
            pricing: null,
        }));

        if (manufacturerId) {
            try {
                const result = await ContactLensConfigurationService.getModelsWithLimit(0, 1000, [], []);
                if (result?.code === 0) {
                    const filteredModels = result.data?.rows?.filter(
                        m => m.manufacturerId === manufacturerId && m.status === 'active'
                    ) || [];
                    setModels(filteredModels);
                }
            } catch (error) {
                if (isLocalDev) console.error(error);
                toast.error('Failed to load contact lens models');
            }
        } else {
            setModels([]);
        }
    };

    // Handle field changes
    const handleConfigChange = (field, value, eye) => {
        const updateConfig = eye === 'OD' ? setOdConfig : setOsConfig;
        updateConfig(prev => ({
            ...prev,
            [field]: value,
            // Reset pricing when configuration changes (except notes)
            pricing: field === 'notes' ? prev.pricing : null,
        }));
    };

    // Increment or decrement box quantity
    const changeBoxQuantity = (eye, delta) => {
        const config = eye === 'OD' ? odConfig : osConfig;
        const current = parseInt(config.boxQuantity, 10) || 1;
        // Ensure next value is at least 1
        const next = Math.max(1, current + delta);
        // Only update if next value would be >= 1
        if (next >= 1) {
            // Use handleConfigChange so pricing resets appropriately
            handleConfigChange('boxQuantity', next, eye);
        }
    };

    // Copy configuration from one eye to the other
    const handleCopyConfig = (fromEye) => {
        if (fromEye === 'OD') {
            setOsConfig({ ...odConfig, pricing: null });
            // Copy the models array from OD to OS
            setOsContactLensModels([...odContactLensModels]);
        } else {
            setOdConfig({ ...osConfig, pricing: null });
            // Copy the models array from OS to OD
            setOdContactLensModels([...osContactLensModels]);
        }
    };

    // Calculate pricing when configuration is complete
    const calculatePricing = async (config, eye) => {
        // Skip pricing calculation for trials
        if (config.useTrial) return;
        const { contactLensModelId, scheduleId, boxQuantity } = config;

        if (!contactLensModelId || !scheduleId || !boxQuantity) {
            return;
        }

        setLoading(true);
        try {
            const result = await ContactLensConfigurationService.getPricing(
                contactLensModelId, 
                scheduleId, 
                boxQuantity
            );

            if (result.code === 0 && result.data) {
                const updateConfig = eye === 'OD' ? setOdConfig : setOsConfig;
                updateConfig(prev => ({
                    ...prev,
                    pricing: result.data
                }));
            } else {
                toast.warning(`No pricing found for ${eye} configuration`);
            }
        } catch (error) {
            if (isLocalDev) console.error(error);
            toast.error('Failed to calculate pricing');
        } finally {
            setLoading(false);
        }
    };

    // Trigger pricing calculation when config is complete
    useEffect(() => {
        if (odConfig.contactLensModelId && !odConfig.useTrial && odConfig.scheduleId && odConfig.boxQuantity) {
            calculatePricing(odConfig, 'OD');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [odConfig.contactLensModelId, odConfig.scheduleId, odConfig.boxQuantity, odConfig.useTrial]);

    useEffect(() => {
        if (osConfig.contactLensModelId && !osConfig.useTrial && osConfig.scheduleId && osConfig.boxQuantity) {
            calculatePricing(osConfig, 'OS');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [osConfig.contactLensModelId, osConfig.scheduleId, osConfig.boxQuantity, osConfig.useTrial]);

    // Get selected model details
    const getSelectedModel = (modelId, eye) => {
        const models = eye === 'OD' ? odContactLensModels : osContactLensModels;
        return models.find(m => m.id === modelId);
    };

    const getSelectedSchedule = (scheduleId) => {
        return schedules.find(s => s.id === scheduleId);
    };

    const handleSave = () => {
        // Preserve existing groupId when editing so downstream update logic can find the items
        const groupId = existingContactLensData?.groupId || uuidv4();     

        // Ensure an Rx is selected (either provided by parent or selected here)
        const rx = selectedRx || localSelectedRx;
        if (!rx) {
            toast.error('Please select an Rx before configuring contact lenses');
            return;
        }

        // Validate that at least one eye is configured
        // Allow trials without schedule/pricing when useTrial is true
        const hasOdConfig = odConfig.contactLensModelId && (odConfig.useTrial || (odConfig.scheduleId && odConfig.pricing));
        const hasOsConfig = osConfig.contactLensModelId && (osConfig.useTrial || (osConfig.scheduleId && osConfig.pricing));

        if (!hasOdConfig && !hasOsConfig) {
            toast.error('Please configure at least one eye');
            return;
        }

        // Prepare contact lens data for invoice
        const contactLensData = {
            od: null,
            os: null
        };

        if (hasOdConfig) {
            const odModel = getSelectedModel(odConfig.contactLensModelId, 'OD');
            const odSchedule = getSelectedSchedule(odConfig.scheduleId);
            
            contactLensData.od = {
                manufacturerId: odConfig.manufacturerId,
                contactLensModelId: odConfig.contactLensModelId,
                scheduleId: odConfig.scheduleId || null,
                boxQuantity: odConfig.boxQuantity,
                cost: odConfig.useTrial ? 0 : (odConfig.pricing?.baseCost || 0),
                retailPrice: odConfig.useTrial ? 0 : (odConfig.pricing?.baseRetailPrice || 0),
                notes: odConfig.notes,
                isTrial: !!odConfig.useTrial,
                modelName: odModel?.modelName,
                scheduleName: odSchedule?.name,
                daysSupply: odSchedule?.daysSupply,
                lensesPerBox: odConfig.pricing?.lensesPerBox || odModel?.lensesPerBox || null
            };
        }

        if (hasOsConfig) {
            const osModel = getSelectedModel(osConfig.contactLensModelId, 'OS');
            const osSchedule = getSelectedSchedule(osConfig.scheduleId);
            
            contactLensData.os = {
                manufacturerId: osConfig.manufacturerId,
                contactLensModelId: osConfig.contactLensModelId,
                scheduleId: osConfig.scheduleId || null,
                boxQuantity: osConfig.boxQuantity,
                cost: osConfig.useTrial ? 0 : (osConfig.pricing?.baseCost || 0),
                retailPrice: osConfig.useTrial ? 0 : (osConfig.pricing?.baseRetailPrice || 0),
                notes: osConfig.notes,
                isTrial: !!osConfig.useTrial,
                modelName: osModel?.modelName,
                scheduleName: osSchedule?.name,
                daysSupply: osSchedule?.daysSupply,
                lensesPerBox: osConfig.pricing?.lensesPerBox || osModel?.lensesPerBox || null
            };
        }

        // include the selected rx so caller can attach it to invoice items
        contactLensData.rx = rx;
        contactLensData.groupId = groupId;

        onSave(contactLensData);
        onClose();
    };

    const renderEyeConfiguration = (eye, config, setConfig) => {
        const models = eye === 'OD' ? odContactLensModels : osContactLensModels;
        const selectedSchedule = getSelectedSchedule(config.scheduleId);

        return (
            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {eye} Configuration
                    </Typography>
                    <Tooltip title={`Copy to ${eye === 'OD' ? 'OS' : 'OD'}`}>
                        <IconButton
                            size="small"
                            onClick={() => handleCopyConfig(eye)}
                            color="primary"
                        >
                            <ContentCopyIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Grid container spacing={2}>
                    {/* Manufacturer */}
                    <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Manufacturer *</InputLabel>
                            <Select
                                value={config.manufacturerId || ''}
                                onChange={(e) => handleManufacturerChange(e.target.value, eye)}
                                label="Manufacturer *"
                            >
                                {manufacturers.map((mfr) => (
                                    <MenuItem key={mfr.id} value={mfr.id}>
                                        {mfr.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Contact Lens Model */}
                    <Grid item xs={12}>
                        <FormControl fullWidth size="small" disabled={!config.manufacturerId}>
                            <InputLabel>Contact Lens Model *</InputLabel>
                            <Select
                                value={config.contactLensModelId || ''}
                                onChange={(e) => handleConfigChange('contactLensModelId', e.target.value, eye)}
                                label="Contact Lens Model *"
                            >
                                {models.map((model) => (
                                    <MenuItem key={model.id} value={model.id}>
                                        {model.modelName}
                                        {model.lensType && ` - ${model.lensType.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}`}
                                        {model.isTrialAvailable ? ' (Trial)' : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Trial indicator and option */}
                    <Grid item xs={12}>
                        {config.contactLensModelId && (() => {
                            const sel = getSelectedModel(config.contactLensModelId, eye);
                            if (!sel) return null;
                            if (!sel.isTrialAvailable) return null;

                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label={`Trial (${sel.trialQuantity ?? '∞'})`} color="secondary" size="small" />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!config.useTrial}
                                                onChange={(e) => handleConfigChange('useTrial', e.target.checked, eye)}
                                                color="primary"
                                            />
                                        }
                                        label="Use trial"
                                    />
                                </Box>
                            );
                        })()}
                    </Grid>

                    {/* Schedule */}
                    <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Wear Schedule (optional for trials)</InputLabel>
                            <Select
                                value={config.scheduleId || ''}
                                onChange={(e) => handleConfigChange('scheduleId', e.target.value, eye)}
                                label="Wear Schedule (optional for trials)"
                            >
                                {schedules.map((schedule) => (
                                    <MenuItem key={schedule.id} value={schedule.id}>
                                        {schedule.name} ({schedule.daysSupply} days)
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Box Quantity */}
                    <Grid item xs={12}>
                        <TextField
                            label="Box Quantity *"
                            type="text"
                            fullWidth
                            size="small"
                            value={config.boxQuantity}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d+$/.test(value)) {
                                    const numValue = value === '' ? 1 : parseInt(value);
                                    if (numValue > 0) {
                                        handleConfigChange('boxQuantity', numValue, eye);
                                    }
                                }
                            }}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Box sx={{ display: 'flex' }}>
                                            <IconButton size="small" onClick={() => changeBoxQuantity(eye, -1)}>
                                                <RemoveIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => changeBoxQuantity(eye, 1)}>
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    {/* Notes */}
                    <Grid item xs={12}>
                        <TextField
                            label="Notes"
                            multiline
                            rows={2}
                            fullWidth
                            size="small"
                            value={config.notes}
                            onChange={(e) => handleConfigChange('notes', e.target.value, eye)}
                        />
                    </Grid>

                    {/* Pricing Summary */}
                    {config.pricing && (
                        <>
                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Pricing Summary
                                </Typography>
                            </Grid>

                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Lenses per Box:
                                </Typography>
                                <Typography variant="body1">
                                    {config.pricing.lensesPerBox}
                                </Typography>
                            </Grid>

                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Price per Box:
                                </Typography>
                                <Typography variant="body1">
                                    {formatAmountForDisplay(config.pricing.baseRetailPrice)}
                                </Typography>
                            </Grid>

                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Total Lenses:
                                </Typography>
                                <Typography variant="body1">
                                    {config.pricing.lensesPerBox * config.boxQuantity}
                                </Typography>
                            </Grid>

                            {selectedSchedule && (
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Supply Duration:
                                    </Typography>
                                    <Typography variant="body1">
                                        {Math.floor((config.pricing.lensesPerBox * config.boxQuantity * selectedSchedule.daysSupply) / 1)} days
                                    </Typography>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 1, borderRadius: 1, textAlign: 'center' }}>
                                    <Typography variant="subtitle2">
                                        Total for {eye}
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        {formatAmountForDisplay(config.pricing.totalRetail)}
                                    </Typography>
                                </Box>
                            </Grid>
                        </>
                    )}
                </Grid>
            </Paper>
        );
    };

    const grandTotal = useMemo(() => {
        const odTotal = odConfig.pricing?.totalRetail || 0;
        const osTotal = osConfig.pricing?.totalRetail || 0;
        return odTotal + osTotal;
    }, [odConfig.pricing, osConfig.pricing]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <AppDialogTitle 
                title={existingContactLensData?.OD ? "Edit Contact Lens Configuration" : "Configure Contact Lenses"} 
                onClose={onClose} 
            />
            <DialogContent>
                <Box display="flex" alignItems="baseline" sx={{ p: 1, borderBottom: '1px solid #ccc', backgroundColor: '#f9f9f9', pb: 1 }}>
                    <CustomerInfobar customerData={customer} />
                </Box>
                {/* RX selection section - contact lenses require an Rx */}
                <RxSection
                    selectedRx={localSelectedRx}
                    setRxSelectDialogOpen={setRxSelectDialogOpen}
                    setCreateRxDialogOpen={setCreateRxDialogOpen}
                    rxType={'contacts'}
                />

                <Grid container spacing={3}>
                    {/* OD Configuration */}
                    <Grid item xs={12} md={6}>
                        {renderEyeConfiguration('OD', odConfig, setOdConfig)}
                    </Grid>

                    {/* OS Configuration */}
                    <Grid item xs={12} md={6}>
                        {renderEyeConfiguration('OS', osConfig, setOsConfig)}
                    </Grid>
                </Grid>

                {/* Grand Total */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'black', color: 'white', borderRadius: 1, textAlign: 'right' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        Grand Total (OD + OS): {formatAmountForDisplay(grandTotal)}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleSave} variant="contained" disabled={loading}>
                    {existingContactLensData?.OD ? 'Update' : 'Add to Invoice'}
                </Button>
                <Button onClick={onClose} variant="outlined" color="cancel">
                    Cancel
                </Button>
            </DialogActions>
            <SelectRxDialog
                open={rxSelectDialogOpen}
                onClose={() => setRxSelectDialogOpen(false)}
                onSelect={(rx) => { setLocalSelectedRx(rx); setRxSelectDialogOpen(false); }}
                customerId={customer.customer?.id}
            />
            <CreateRxInlineDialog
                open={createRxDialogOpen}
                onClose={() => setCreateRxDialogOpen(false)}
                customerId={customer.customer?.id}
                onRxCreated={(newRx) => { setLocalSelectedRx(newRx); setCreateRxDialogOpen(false); }}
            />
        </Dialog>
    );
}
